import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  ChevronRightIcon,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import {
  APIReferenceMember,
  MemberType,
  MemberTypeListNameTranslKey,
  ReferenceType,
} from '@modules/client';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { getReferencePageUrl } from '../../utils/apiReferencePageNavUtils';
import ReferenceSummaryMemberTableV2 from './ReferenceSummaryMemberTableV2';
import useReferenceInheritedMembersStyles from './ReferenceInheritedMembers.styles';
import ReferenceSummarySectionTitle from './ReferenceSummarySectionTitle';

type InheritedGroup = {
  parent: string;
  total: number;
  membersByType: Record<MemberType, APIReferenceMember[]>;
};

interface ReferenceInheritedMembersProps {
  referenceType: ReferenceType;
  groups: InheritedGroup[];
}

export default function ReferenceInheritedMembers({
  referenceType,
  groups,
}: ReferenceInheritedMembersProps) {
  const { translate, translateHTML } = useTranslation();
  const {
    classes: {
      container,
      titleLink,
      inheritedGroupTitle,
      accordionGroup,
      summary,
      details,
      detailsContent,
    },
  } = useReferenceInheritedMembersStyles();

  if (groups.length === 0) return null;

  return (
    <div className={container}>
      <ReferenceSummarySectionTitle
        title={translate('Label.InheritedMembers') || 'Inherited Members'}
      />
      {groups.map((group) => (
        <Accordion
          key={group.parent}
          slotProps={{
            transition: {
              mountOnEnter: true,
              timeout: 200,
            },
          }}
          disableGutters
          className={accordionGroup}>
          <AccordionSummary expandIcon={<ChevronRightIcon />} className={summary}>
            <Typography variant='body2' className={inheritedGroupTitle}>
              {translateHTML('Label.CountInheritedFromLinkedName', [
                {
                  opening: 'countStart',
                  closing: 'countEnd',
                  content: () => `${group.total}`,
                },
                {
                  opening: 'parentLinkStart',
                  closing: 'parentLinkEnd',
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content: () => (
                    <DynamicallyRouted.Link
                      href={getReferencePageUrl(referenceType, group.parent)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      className={titleLink}>
                      {group.parent}
                    </DynamicallyRouted.Link>
                  ),
                },
              ]) || `${group.total} inherited from ${group.parent}`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={details}>
            <div className={detailsContent}>
              {(Object.keys(group.membersByType) as Array<MemberType>).map((memberType) => {
                const members = group.membersByType[memberType] || [];
                if (!members.length) return null;
                return (
                  <ReferenceSummaryMemberTableV2
                    key={`${group.parent}-${memberType}`}
                    memberType={memberType}
                    referenceType={referenceType}
                    title={translate(MemberTypeListNameTranslKey[memberType]) || memberType}
                    referenceName={group.parent}
                    isInheritedMembers
                    members={members}
                    isEventMemberType={memberType === MemberType.Event}
                  />
                );
              })}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}
